import React, { useState, useMemo } from 'react';
import {
  useParams, useNavigate, useOutletContext, Link,
} from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LinearProgress from '@mui/material/LinearProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import IntegrationRequestModal from './IntegrationRequestModal';

const TYPES = ['wms', 'cart', 'carrier'];
const TYPE_LABELS = { wms: 'WMS', cart: 'Cart', carrier: 'Carrier' };
const snakeToTitle = (str) => str.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
const toPath = (resource) => `/${resource.replace(/_/g, '-')}`;
const DOCS_BASE = 'https://docs.trackstarhq.com/api-reference';

// child resources that should be merged into their parent's card
const CHILD_RESOURCES = {
  order_channels: {
    parent: 'orders', label: 'List Channels', endpoint: 'GET /orders/channels', docsSlug: 'order-channels-get',
  },
  order_packs: {
    parent: 'orders', label: 'List Packs', endpoint: 'GET /orders/packs', docsSlug: 'get-packs',
  },
  inbound_shipment_suppliers: {
    parent: 'inbound_shipments', label: 'List Suppliers', endpoint: 'GET /inbound-shipments/suppliers', docsSlug: 'inbound-shipment-suppliers-get',
  },
  warehouse_locations: {
    parent: 'warehouses', label: 'List Locations', endpoint: 'GET /warehouses/locations', docsSlug: 'get-warehouse-locations',
  },
  inventory_ledger: {
    parent: 'inventory', label: 'List Ledger', endpoint: 'GET /inventory/ledger', docsSlug: 'inventory-ledger',
  },
};

// compound actions that don't follow verb_resource pattern
// resource values must match backend name() output
const ACTION_OVERRIDES = {
  create_shipment: {
    resource: 'orders', label: 'Create Shipment', endpoint: 'POST /orders/{id}/shipment', docsSlug: 'create-shipment',
  },
  create_order_shipment: {
    resource: 'orders', label: 'Create Shipment', endpoint: 'POST /orders/{id}/shipment', docsSlug: 'create-shipment',
  },
  attach_order_file: {
    resource: 'orders', label: 'Attach File', endpoint: 'POST /orders/{id}/file', docsSlug: 'attach-file',
  },
  create_inbound_shipment_receipt: {
    resource: 'inbound_shipments', label: 'Create Receipt', endpoint: 'POST /inbound-shipments/{id}/receipt', docsSlug: 'receive',
  },
  create_receipt: {
    resource: 'inbound_shipments', label: 'Create Receipt', endpoint: 'POST /inbound-shipments/{id}/receipt', docsSlug: 'receive',
  },
  refund_cart_order: {
    resource: 'orders', label: 'Refund', endpoint: 'POST /orders/{id}/refund', docsSlug: 'refund',
  },
  refund_order: {
    resource: 'orders', label: 'Refund', endpoint: 'POST /orders/{id}/refund', docsSlug: 'refund',
  },
  adjust_inventory: {
    resource: 'inventory', label: 'Adjust', endpoint: 'POST /inventory/adjust', docsSlug: 'adjust',
  },
  adjust_cart_product_inventory: {
    resource: 'products', label: 'Adjust Inventory', endpoint: 'POST /products/{id}/adjust-inventory', docsSlug: 'adjust',
  },
};

const STANDARD_VERBS = ['get', 'list', 'create', 'update', 'delete', 'cancel'];
const VERB_TO_METHOD = {
  get: 'GET', list: 'GET', create: 'POST', update: 'PUT', delete: 'DELETE', cancel: 'POST',
};
const VERB_TO_DOCS_SLUG = {
  get: 'get-item', list: 'get', create: 'post', update: 'put', delete: 'delete', cancel: 'cancel',
};

const singularize = (str) => {
  if (str.endsWith('ies')) return `${str.slice(0, -3)}y`;
  if (str.endsWith('s') && !str.endsWith('ss')) return str.slice(0, -1);
  return str;
};

const buildDocsUrl = (integrationType, resource, docsSlug, docsResource) => {
  const typeSlug = `${integrationType}-api`;
  const resourceSlug = (docsResource || resource).replace(/_/g, '-');
  return `${DOCS_BASE}/${typeSlug}/${resourceSlug}/${docsSlug}`;
};

const groupActions = (allActions, supportedActions, integrationType) => {
  const supportedSet = new Set(supportedActions);

  // discover resources from get_ actions, resolving children to parents
  const allResources = allActions
    .filter((a) => a.startsWith('get_'))
    .map((a) => a.slice(4));
  const topResources = allResources.filter((r) => !CHILD_RESOURCES[r]);

  const groups = {};
  const add = (resource, action, label, endpoint, docsSlug, docsResource) => {
    if (!groups[resource]) groups[resource] = [];
    groups[resource].push({
      action,
      label,
      endpoint,
      docsUrl: buildDocsUrl(integrationType, resource, docsSlug, docsResource),
      isSupported: supportedSet.has(action),
    });
  };

  allActions.forEach((action) => {
    // 1) explicit overrides
    if (ACTION_OVERRIDES[action]) {
      const o = ACTION_OVERRIDES[action];
      add(o.resource, action, o.label, o.endpoint, o.docsSlug);
      return;
    }

    // 2) get_ actions: merge children into parent, keep top-level as-is
    if (action.startsWith('get_')) {
      const resourceName = action.slice(4);
      const child = CHILD_RESOURCES[resourceName];
      if (child) {
        add(child.parent, action, child.label, child.endpoint, child.docsSlug, child.docsResource);
      } else {
        add(resourceName, action, 'List', `GET ${toPath(resourceName)}`, 'get');
      }
      return;
    }

    // 3) standard verb_resource against top-level resources only
    const parts = action.split('_');
    if (STANDARD_VERBS.includes(parts[0])) {
      const verb = parts[0];
      const rest = parts.slice(1).join('_');
      const match = topResources.find((r) => r === rest || singularize(r) === rest);
      if (match) {
        const method = VERB_TO_METHOD[verb];
        const needsId = ['get', 'update', 'delete', 'cancel'].includes(verb);
        const suffix = verb === 'cancel' ? '/cancel' : '';
        const path = needsId
          ? `${toPath(match)}/{id}${suffix}`
          : toPath(match);
        add(match, action, verb.charAt(0).toUpperCase() + verb.slice(1), `${method} ${path}`, VERB_TO_DOCS_SLUG[verb]);
        return;
      }
    }

    // 4) substring fallback against top-level resources (longest match first)
    const sorted = [...topResources].sort((a, b) => b.length - a.length);
    const match = sorted.find((r) => action.includes(singularize(r)) || action.includes(r));
    if (match) {
      add(match, action, snakeToTitle(action), snakeToTitle(action), 'get');
      return;
    }

    // 5) total fallback
    add('other', action, snakeToTitle(action), snakeToTitle(action), 'get');
  });

  // sort groups alphabetically by resource name
  return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
};

function IntegrationDetail() {
  const { type, name } = useParams();
  const { integrationsByType, actionsByType, loading } = useOutletContext();

  const integration = (integrationsByType[type] || []).find(
    (i) => i.integration_name === name,
  );

  const groups = useMemo(() => {
    if (!integration) return [];
    const allActions = actionsByType[type] || [];
    return groupActions(allActions, integration.available_actions, type);
  }, [integration, actionsByType, type]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 gap-3">
        <LinearProgress sx={{ width: 200 }} />
        <p className="text-sm text-surface-500">Loading integration...</p>
      </div>
    );
  }

  if (!integration) {
    return (
      <div className="flex flex-col h-full">
        <Link to="/integrations" className="text-sm text-surface-500 hover:text-surface-700">
          &larr; Back to integrations
        </Link>
        <p className="mt-4 text-surface-500">Integration not found</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* header */}
      <div className="flex items-center justify-between shrink-0 pr-2">
        <div className="flex items-center gap-4">
          <Link
            to="/integrations"
            className="text-sm text-surface-500 hover:text-surface-700 transition-colors"
          >
            &larr; Back
          </Link>
          <div className="w-px h-5 bg-surface-200" />
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md bg-surface-100 flex items-center justify-center border border-surface-200">
              <img src={integration.logo_url} alt={integration.display_name} className="h-5 w-5" />
            </div>
            <h1 className="text-xl font-semibold text-surface-900 tracking-tight">{integration.display_name}</h1>
            <span className="text-xs text-surface-400">{TYPE_LABELS[type]}</span>
          </div>
        </div>
        <p className="text-xs text-surface-400 text-right max-w-[280px]">
          Unsupported actions may still be available.
          {' '}
          Contact
          {' '}
          <a href="mailto:support@trackstarhq.com" className="underline hover:text-surface-600">
            support@trackstarhq.com
          </a>
          {' '}
          to verify.
        </p>
      </div>

      {/* resource cards */}
      <div className="mt-5 flex-1 min-h-0 overflow-auto pr-2">
        <div
          className="grid grid-cols-2 xl:grid-cols-3 gap-3"
          style={{
            gridAutoRows: `${Math.max(...groups.map(([, a]) => a.length)) * 26 + 52}px`,
          }}
        >
          {groups.map(([resource, actions]) => {
            const groupSupported = actions.filter((a) => a.isSupported).length;
            return (
              <div
                key={resource}
                className="rounded-xl border border-surface-200 bg-white p-4 flex flex-col"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-surface-800">
                    {snakeToTitle(resource)}
                  </h3>
                  <span className="text-xs font-medium text-surface-500">
                    {groupSupported}
                    /
                    {actions.length}
                  </span>
                </div>
                <div className="grid grid-cols-[14px_auto_1fr] gap-x-3 gap-y-1 items-center">
                  {actions.map(({
                    action, label, endpoint, docsUrl, isSupported,
                  }) => (
                    <React.Fragment key={action}>
                      {isSupported ? (
                        <CheckIcon sx={{ fontSize: 14, color: '#059669' }} />
                      ) : (
                        <CloseIcon sx={{ fontSize: 14, color: '#d1d5db' }} />
                      )}
                      <span className={`text-[13px] ${isSupported ? 'text-surface-700' : 'text-surface-400'}`}>
                        {label}
                      </span>
                      <a
                        href={docsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-[11px] font-mono whitespace-nowrap underline decoration-dotted ${isSupported ? 'text-surface-400 hover:text-surface-600' : 'text-surface-300 hover:text-surface-400'}`}
                      >
                        {endpoint}
                      </a>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Actions() {
  const [search, setSearch] = useState('');
  const [activeType, setActiveType] = useState('wms');
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const navigate = useNavigate();
  const { integrationsByType, loading } = useOutletContext();

  const integrations = integrationsByType[activeType] || [];
  const filtered = search
    ? integrations.filter((i) => i.display_name.toLowerCase().includes(search.toLowerCase()))
    : integrations;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between shrink-0">
        <div className="flex items-baseline gap-3">
          <h1 className="page-title">Integrations</h1>
          <span className="page-subtitle">Select an integration to view available actions</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-60">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search integrations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-9 !py-2"
            />
          </div>
          <button
            type="button"
            onClick={() => setRequestModalOpen(true)}
            className="btn btn-accent"
          >
            Request new integration
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center flex-1 gap-3">
          <LinearProgress sx={{ width: 200 }} />
          <p className="text-sm text-surface-500">Loading integrations...</p>
        </div>
      ) : (
        <>
          <div className="flex gap-1 mt-4 shrink-0 border-b border-surface-200">
            {TYPES.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => { setActiveType(t); setSearch(''); }}
                className={`px-4 py-2 text-sm font-medium transition-colors cursor-pointer -mb-px ${
                  activeType === t
                    ? 'text-surface-900 border-b-2 border-surface-900'
                    : 'text-surface-400 hover:text-surface-600'
                }`}
              >
                {TYPE_LABELS[t]}
                <span className={`ml-1.5 text-xs ${activeType === t ? 'text-surface-500' : 'text-surface-300'}`}>
                  {(integrationsByType[t] || []).length}
                </span>
              </button>
            ))}
          </div>

          <div className="flex-1 min-h-0 overflow-auto mt-4 pr-2">
            {search && filtered.length === 0 && (
              <div className="flex flex-col items-center justify-center h-40">
                <p className="text-sm text-surface-500">
                  No integrations match &ldquo;
                  {search}
                  &rdquo;
                </p>
              </div>
            )}
            <div className="grid grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              {filtered.map((integration) => (
                <button
                  type="button"
                  key={integration.integration_name}
                  className="card card-hover p-3 flex items-center gap-3 cursor-pointer text-left"
                  onClick={() => navigate(`/integrations/${activeType}/${integration.integration_name}`)}
                >
                  <div className="w-7 h-7 rounded-md bg-surface-100 flex items-center justify-center border border-surface-200 flex-shrink-0">
                    <img
                      src={integration.logo_url}
                      alt={integration.display_name}
                      className="h-4 w-4"
                    />
                  </div>
                  <span className="text-sm font-medium text-surface-800 truncate">
                    {integration.display_name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      <IntegrationRequestModal
        isOpen={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        onSuccess={() => setToastOpen(true)}
      />
      <Snackbar
        open={toastOpen}
        autoHideDuration={5000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity="success" variant="filled">
          Request submitted. We&rsquo;ll be in touch.
        </Alert>
      </Snackbar>
    </div>
  );
}

export default Actions;
export { IntegrationDetail };
